import { FC, useContext, useEffect, useState } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { isLeft } from "fp-ts/lib/Either";
import { useForm } from "react-hook-form";
import LocalizationContextType from "../../../../../domain/providers/localization/LocalizationContextType";
import ModalsContextType from "../../../../../domain/providers/modal/ModalsContextType";
import LocalizationContext from "../../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../../providers/modal/ModalsContext";
import KeyWordLocalization from "../../../../utils/KeyWordLocalization";
import Validators from "../../../../utils/Validators";
import CreateUserComponentProps from "./create_user.component.props";
import CreateUserUseCase, { CreateUserUseCaseName } from "../../../../../domain/use_cases/user/CreateUserUseCase";
import di from "../../../../../DependencyInjection";
import UserEntity, { UserType } from "../../../../../domain/entities/UserEntity";
import UpdateUserUseCase, { UpdateUserUseCaseName } from "../../../../../domain/use_cases/user/UpdateUserUseCase";
import ButtonComponent from "../../../../components/button/ButtonComponent";
import EntitiesConstants from "../../../../utils/EntitiesConstants";
import SearchUsersUseCase, { SearchUsersUseCaseName } from "../../../../../domain/use_cases/user/SearchUsersUseCase";
import { AutoCompleteItem } from "../../../../components/autocompleteWithCheck/AutoCompleteWithCheckProps";
import AutoCompleteWithCheckComponent from "../../../../components/autocompleteWithCheck/AutoCompleteWithCheckComponent";
import StablishmentEntity from "../../../../../domain/entities/StablishmentEntity";
import SearchStablishmentsUseCase, { SearchStablishmentsUseCaseName } from "../../../../../domain/use_cases/stablishment/SearchStablishmentsUseCase";

const CreateUserComponent: FC<CreateUserComponentProps> = ({
  onCreated,
  onUpdated,
  user,
}) => {
  const formInfo = useForm();
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = formInfo;
  const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
  const { addToast } = useContext(ModalsContext) as ModalsContextType;
  const [loading, setLoading] = useState<boolean>(false);

  // Observar cambios en el rol
  const selectedRole = watch(EntitiesConstants.UserEntityRole);

  // Función para buscar establecimientos
  const _onSearchStablishments = async (query: string): Promise<AutoCompleteItem<StablishmentEntity>[]> => {
    const response = await di.get<SearchStablishmentsUseCase>(SearchStablishmentsUseCaseName).call({
      query,
      page: 1,
      itemsPerPage: 20,
    });
    if (isLeft(response)) return [];
    return response.right.data.map((item) => ({
      label: `${item.name} - ${item.email || 'Sin email'}`,
      id: item.id,
      aditionalValue: item,
    }));
  };

  const _handleCreateUser = async (data: any) => {
    setLoading(true);

    const tempUser: UserEntity = {
      id: data[EntitiesConstants.UserEntityId] || "",
      name: data[EntitiesConstants.UserEntityName],
      prefix: data[EntitiesConstants.UserEntityPrefix],
      phone: data[EntitiesConstants.UserEntityPhone],
      username: data[EntitiesConstants.UserEntityUsername],
      role: data[EntitiesConstants.UserEntityRole],
      email: data[EntitiesConstants.UserEntityEmail],
      document: data[EntitiesConstants.UserEntityDocument],
      documentType: data[EntitiesConstants.UserEntityDocumentType],
    }
    const response = await di
      .get<CreateUserUseCase>(CreateUserUseCaseName)
      .call({
        user: tempUser,
        password: data[EntitiesConstants.UserEntityPassword],
      });

    if (isLeft(response)) {
      addToast(i18n(response.left.code ?? KeyWordLocalization.CreateUserPageCreateError), "error", undefined);
      setLoading(false);
      return;
    }

    addToast(i18n(KeyWordLocalization.CreateUserPageCreateSuccess), "success", undefined);
    onCreated?.(response.right);
    setLoading(false);
  };

  const _loadInfo = async () => {
    if (user == null) return;
    setValue(EntitiesConstants.UserEntityName, user.name);
    setValue(EntitiesConstants.UserEntityPrefix, user.prefix || '+1 809');
    setValue(EntitiesConstants.UserEntityPhone, user.phone);
    setValue(EntitiesConstants.UserEntityUsername, user.username);
    setValue(EntitiesConstants.UserEntityRole, user.role);
    setValue(EntitiesConstants.UserEntityDocument, user.document);
    setValue(EntitiesConstants.UserEntityDocumentType, user.documentType);
    setValue(EntitiesConstants.UserEntityEmail, user.email);    
  };

  const _handleOnEdit = async (data: any) => {
    if (!user) return;

    console.log("data to update", data);
    setLoading(true);

    const tempUser: UserEntity = {
      id: user.id,
      name: data[EntitiesConstants.UserEntityName],
      prefix: data[EntitiesConstants.UserEntityPrefix],
      phone: data[EntitiesConstants.UserEntityPhone],
      username: data[EntitiesConstants.UserEntityUsername],
      role: data[EntitiesConstants.UserEntityRole],
      document: data[EntitiesConstants.UserEntityDocument],
      documentType: data[EntitiesConstants.UserEntityDocumentType],
      email: data[EntitiesConstants.UserEntityEmail],
    }

    const response = await di
      .get<UpdateUserUseCase>(UpdateUserUseCaseName)
      .call({
        user: tempUser,
        password: data[EntitiesConstants.UserEntityPassword] || undefined,
      });

    if (isLeft(response)) {
      addToast(i18n(KeyWordLocalization.CreateUserPageCreateError), "error", undefined);
      setLoading(false);
      return;
    }

    addToast(i18n(KeyWordLocalization.CreateUserPageCreateSuccess), "success", undefined);
    onUpdated?.(response.right);
    setLoading(false);
  };


  useEffect(() => {
    _loadInfo();
  }, [user]);

  return <div className="w-100 overflow-x-hidden">
    <form
      className="row"
      onSubmit={handleSubmit(
        user == null ? _handleCreateUser : _handleOnEdit
      )}
    >

      <div
        className={`form-group col-md-12 my-3 ${errors[EntitiesConstants.UserEntityName] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n("Nombre Completo")}
        </label>
        <input
          type="text"
          disabled={loading}
          className="form-control"
          placeholder={i18n("Ingrese el nombre completo")}
          {...register(
            EntitiesConstants.UserEntityName,
            Validators({ required: true, noNumber: true })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.UserEntityName}
        />
      </div>

      <div
        className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.UserEntityUsername] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n("Usuario")}
        </label>
        <input
          type="text"
          disabled={loading}
          className="form-control"
          placeholder={i18n("Nombre de usuario")}
          {...register(
            EntitiesConstants.UserEntityUsername,
            Validators({ required: true })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.UserEntityUsername}
        />
      </div>

      <div
        className={`form-group col-md-2 my-3 ${errors[EntitiesConstants.UserEntityPrefix] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n("Prefijo")}
        </label>
        <select
          disabled={loading}
          className="form-control"
          defaultValue="1 809"
          {...register(
            EntitiesConstants.UserEntityPrefix,
            Validators({ required: true })
          )}
        >
          <option value="1809">🇩🇴 +1 809</option>
          <option value="1829">🇩🇴 +1 829</option>
          <option value="1849">🇩🇴 +1 849</option>
          <option value="1">🇺🇸 +1</option>
          <option value="34">🇪🇸 +34</option>
          <option value="52">🇲🇽 +52</option>
          <option value="57">🇨🇴 +57</option>
          <option value="54">🇦🇷 +54</option>
          <option value="56">🇨🇱 +56</option>
          <option value="51">🇵🇪 +51</option>
          <option value="58">🇻🇪 +58</option>
          <option value="507">🇵🇦 +507</option>
        </select>
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.UserEntityPrefix}
        />
      </div>

      <div
        className={`form-group col-md-4 my-3 ${errors[EntitiesConstants.UserEntityPhone] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n("Teléfono")}
        </label>
        <input
          type="tel"
          disabled={loading}
          className="form-control"
          placeholder={i18n("Número de teléfono")}
          {...register(
            EntitiesConstants.UserEntityPhone,
            Validators({ required: true, mustBeNumber: true, minLength: 7 })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.UserEntityPhone}
        />
      </div>

      <div
        className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.UserEntityDocumentType] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n("Tipo de Documento")}
        </label>
        <select
          disabled={loading}
          className="form-control"
          {...register(
            EntitiesConstants.UserEntityDocumentType,
            Validators({ required: true })
          )}
        >
          <option value="">{i18n("Seleccione el tipo")}</option>
          <option value="cie">{i18n("Cédula de Extranjería")}</option>
          <option value="pasaporte">{i18n("Pasaporte")}</option>
          <option value="licencia">{i18n("Licencia de Conducción")}</option>
          <option value="rnc">{i18n("Registro Nacional de Contribuyentes")}</option>
          <option value="tarjeta_residencia">{i18n("Tarjeta de Residencia")}</option>
        </select>
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.UserEntityDocumentType}
        />
      </div>

      <div
        className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.UserEntityDocument] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n("Número de Documento")}
        </label>
        <input
          type="number"
          disabled={loading}
          className="form-control"
          placeholder={i18n("Número de documento")}
          {...register(
            EntitiesConstants.UserEntityDocument,
            Validators({ required: true, mustBeNumber: true, minLength: 6 })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.UserEntityDocument}
        />
      </div>

      <div
        className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.UserEntityEmail] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n("Email")}
        </label>
        <input
          type="email"
          disabled={loading}
          className="form-control"
          placeholder={i18n("Correo electrónico")}
          {...register(
            EntitiesConstants.UserEntityEmail,
            Validators({ required: true, email: true })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.UserEntityEmail}
        />
      </div>

      <div
        className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.UserEntityRole] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n("Rol")}
        </label>
        <select
          disabled={loading}
          className="form-control"
          {...register(
            EntitiesConstants.UserEntityRole,
            Validators({ required: true })
          )}
        >
          <option value="">{i18n("Seleccione un rol")}</option>
          <option value={UserType.ADMIN}>{i18n("Administrador")}</option>
          <option value={UserType.SUPERVISOR}>{i18n("Supervisor")}</option>
          <option value={UserType.VALET}>{i18n("Valet")}</option>
        </select>
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.UserEntityRole}
        />
      </div>

      <div className="row">
        <div
          className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.UserEntityPassword] && "error error_group"
            }`}
        >
          <label className="form-label">
            {i18n("Contraseña")} {user && <small className="text-muted">({i18n("Opcional - dejar vacío para mantener actual")})</small>}
          </label>
          <input
            type="password"
            disabled={loading}
            className="form-control"
            placeholder={user ? i18n("Nueva contraseña (opcional)") : i18n("Contraseña")}
            {...register(
              EntitiesConstants.UserEntityPassword,
              user
                ? {
                    validate: (value: string) => {
                      if (!value || value.length === 0) return true;
                      if (value.length < 6) return i18n("Mínimo 6 caracteres");
                      if (!value.match(/(?=.*[\^$*.[\]{}()?"!@#%&/\\,><'+r':;|_~`=+\- ])/g)) return i18n("Debe contener al menos un carácter especial");
                      if (!value.match(/(?=.*[A-Z])/g)) return i18n("Debe contener al menos una mayúscula");
                      if (!value.match(/(?=.*[a-z])/g)) return i18n("Debe contener al menos una minúscula");
                      if (!value.match(/(?=.*[0-9])/g)) return i18n("Debe contener al menos un número");
                      return true;
                    }
                  }
                : Validators({ required: true, minLength: 6, isPassword: true })
            )}
          />
          <ErrorMessage
            as="aside"
            errors={errors}
            name={EntitiesConstants.UserEntityPassword}
          />
        </div>
        <div
          className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.UserEntityConfirmPassword] && "error error_group"
            }`}
        >
          <label className="form-label">
            {i18n("Confirmar contraseña")} {user && <small className="text-muted">({i18n("Opcional - dejar vacío para mantener actual")})</small>}
          </label>
          <input
            type="password"
            disabled={loading}
            className="form-control"
            placeholder={user ? i18n("Confirmar contraseña (opcional)") : i18n("Confirmar contraseña")}
            {...register(
              EntitiesConstants.UserEntityConfirmPassword,
              user
                ? {
                    validate: (value: string) => {
                      const password = getValues(EntitiesConstants.UserEntityPassword);
                      if ((!value || value.length === 0) && (!password || password.length === 0)) {
                        return true;
                      }
                      if (!value || value.length === 0) {
                        return i18n("Debe confirmar la contraseña");
                      }
                      if (value.length < 6) return i18n("Mínimo 6 caracteres");
                      if (!value.match(/(?=.*[\^$*.[\]{}()?"!@#%&/\\,><'+r':;|_~`=+\- ])/g)) return i18n("Debe contener al menos un carácter especial");
                      if (!value.match(/(?=.*[A-Z])/g)) return i18n("Debe contener al menos una mayúscula");
                      if (!value.match(/(?=.*[a-z])/g)) return i18n("Debe contener al menos una minúscula");
                      if (!value.match(/(?=.*[0-9])/g)) return i18n("Debe contener al menos un número");
                      if (value !== password) return i18n("Las contraseñas no coinciden");
                      return true;
                    }
                  }
                : Validators({
                    required: true,
                    minLength: 6,
                    isPassword: true,
                    validate: (value: any) => value === getValues(EntitiesConstants.UserEntityPassword) || i18n("Las contraseñas no coinciden")
                  })
            )}
          />
          <ErrorMessage
            as="aside"
            errors={errors}
            name={EntitiesConstants.UserEntityConfirmPassword}
          />
        </div>

      </div>

      <div className="w-100 d-flex justify-content-end my-3">
        {user == null ? (
          <ButtonComponent
            isLoading={loading}
            icon={<span className="material-symbols-outlined">add</span>}
            type="submit"
            design="primaryOutline"
            text={i18n("Crear Usuario")}
          />
        ) : (
          <ButtonComponent
            isLoading={loading}
            icon={<span className="material-symbols-outlined">save</span>}
            type="submit"
            design="primaryOutline"
            text={i18n("Actualizar Usuario")}
          />
        )}
      </div>
    </form>

  </div>
};

export default CreateUserComponent;